// Raben (Polish pallet carrier, SOAP GS1 XML) credential + config block for
// the account panel. Credentials live in the account's own columns
// (loginUsername = SOAP username, password = SOAP password, vatNumber = NIP,
// apiKey = SFTP password for the optional eWZ upload); everything else is
// account.config.raben, written back by PATCH /admin/carriers/accounts/:id
// as the WHOLE config object with `raben` merged in.
//
// The text fields keep a local draft and commit on blur (the settings-page
// convention); the select, the toggle and the service chips commit at once.
// A draft that fails validation is shown with its error and is NOT sent.

import { useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "../../../i18n";
import SecretField from "./SecretField";
import type { CarrierAccount, CarrierAccountPatch, CarrierEnvironment, RabenAccountConfig, RabenProductType } from "./types";

export const RABEN_NOTES_MAX = 10;
export const RABEN_NOTE_MAX_LENGTH = 70;
export const RABEN_SHIP_FROM_MAX_LENGTH = 20;
export const RABEN_RELATION_LENGTH = 11;

export const RABEN_DEFAULTS: RabenAccountConfig = {
    ediSender: "",
    ediReceiver: "Raben Poland",
    department: "",
    payerSearchname: "",
    ediCustomerNumber: "90000050",
    shipFromSearchname: "JLI-POLAND",
    productType: "PROD02",
    services: [],
    notes: [],
    ewz: { enabled: false, host: "edi.raben-group.com", port: 2222, username: "", relationNumber: "", remotePath: "/" },
};

/** The stored config.raben laid over the defaults, so every key exists. */
export const withRabenDefaults = (stored: CarrierAccount["config"]): RabenAccountConfig => {
    const r = stored?.raben ?? {};
    return {
        ...RABEN_DEFAULTS,
        ...r,
        productType: r.productType === "PROD01" ? "PROD01" : "PROD02",
        services: Array.isArray(r.services) ? r.services.map(String) : [],
        notes: Array.isArray(r.notes) ? r.notes.map(String) : [],
        ewz: { ...RABEN_DEFAULTS.ewz, ...(r.ewz ?? {}) },
    };
};

export type RabenErrorKey =
    | "carriers_raben_err_port"
    | "carriers_raben_err_relation"
    | "carriers_raben_err_notes_count"
    | "carriers_raben_err_notes_length"
    | "carriers_raben_err_ship_from_length";

/** Field -> error key; empty when the config can be saved. */
export const validateRaben = (cfg: RabenAccountConfig): Partial<Record<"port" | "relationNumber" | "notes" | "shipFromSearchname", RabenErrorKey>> => {
    const errors: Partial<Record<"port" | "relationNumber" | "notes" | "shipFromSearchname", RabenErrorKey>> = {};
    if (!Number.isInteger(cfg.ewz.port) || cfg.ewz.port < 1 || cfg.ewz.port > 65535) errors.port = "carriers_raben_err_port";
    if (cfg.ewz.enabled && cfg.ewz.relationNumber.length !== RABEN_RELATION_LENGTH) errors.relationNumber = "carriers_raben_err_relation";
    if (cfg.notes.length > RABEN_NOTES_MAX) errors.notes = "carriers_raben_err_notes_count";
    else if (cfg.notes.some((n) => n.length > RABEN_NOTE_MAX_LENGTH)) errors.notes = "carriers_raben_err_notes_length";
    if (cfg.shipFromSearchname.length > RABEN_SHIP_FROM_MAX_LENGTH) errors.shipFromSearchname = "carriers_raben_err_ship_from_length";
    return errors;
};

interface Props {
    account: CarrierAccount;
    environment?: CarrierEnvironment;
    saving: boolean;
    readOnly: boolean;
    patch: (body: CarrierAccountPatch) => Promise<void>;
}

const RabenAccountFields = ({ account, environment, saving, readOnly, patch }: Props) => {
    const { t } = useTranslation();
    const [cfg, setCfg] = useState<RabenAccountConfig>(() => withRabenDefaults(account.config));
    const [notesText, setNotesText] = useState(() => cfg.notes.join("\n"));
    const [portText, setPortText] = useState(() => String(cfg.ewz.port));
    const [serviceDraft, setServiceDraft] = useState("");
    const [errors, setErrors] = useState<ReturnType<typeof validateRaben>>({});
    const disabled = saving || readOnly;

    const saved = withRabenDefaults(account.config);

    // The draft with the notes / port text fields folded in and every string trimmed.
    const current = (over: Partial<RabenAccountConfig> = {}): RabenAccountConfig => {
        const raw = { ...cfg, ...over };
        const portRaw = portText.trim();
        return {
            ...raw,
            ediSender: raw.ediSender.trim(),
            ediReceiver: raw.ediReceiver.trim(),
            department: raw.department.trim(),
            payerSearchname: raw.payerSearchname.trim(),
            ediCustomerNumber: raw.ediCustomerNumber.trim(),
            shipFromSearchname: raw.shipFromSearchname.trim(),
            notes: over.notes ?? notesText.split(/\r?\n/).map((n) => n.trim()).filter(Boolean),
            ewz: {
                ...raw.ewz,
                host: raw.ewz.host.trim(),
                username: raw.ewz.username.trim(),
                relationNumber: raw.ewz.relationNumber.trim(),
                remotePath: raw.ewz.remotePath.trim(),
                port: portRaw === "" ? Number.NaN : Number(portRaw),
            },
        };
    };

    // Validate, then send the whole account config with raben merged in.
    // Nothing is sent while a field is invalid (the error stays on screen)
    // or when the draft equals what is already stored.
    const commit = (next: RabenAccountConfig) => {
        setCfg(next);
        const found = validateRaben(next);
        setErrors(found);
        if (Object.keys(found).length > 0) return;
        if (JSON.stringify(next) === JSON.stringify(saved)) return;
        patch({ config: { ...(account.config ?? {}), raben: next } }).catch(() => {});
    };
    const flush = () => commit(current());
    const commitEwz = (patchEwz: Partial<RabenAccountConfig["ewz"]>) => {
        const now = current();
        commit({ ...now, ewz: { ...now.ewz, ...patchEwz } });
    };

    const textField = (
        key: "ediSender" | "ediReceiver" | "department" | "payerSearchname" | "ediCustomerNumber" | "shipFromSearchname",
        labelKey: string,
        placeholder: string,
    ) => (
        <div className="admin-integrations-field" key={key}>
            <label className="admin-integrations-field-label" htmlFor={`raben-${account.id}-${key}`}>{t(labelKey)}</label>
            <input
                id={`raben-${account.id}-${key}`}
                className="admin-integrations-input admin-integrations-input--mono"
                value={cfg[key]}
                placeholder={placeholder}
                disabled={disabled}
                onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
                onBlur={flush}
            />
            {key === "shipFromSearchname" && errors.shipFromSearchname && (
                <p className="admin-integrations-field-error">{t(errors.shipFromSearchname)}</p>
            )}
        </div>
    );

    const addService = () => {
        const code = serviceDraft.trim().toUpperCase();
        setServiceDraft("");
        if (!code || cfg.services.includes(code)) return;
        commit(current({ services: [...cfg.services, code] }));
    };
    const removeService = (code: string) => commit(current({ services: cfg.services.filter((s) => s !== code) }));

    return (
        <>
            <p className="admin-integrations-field-hint carrier-card-note">
                {t(environment === "live" ? "carriers_raben_live_credentials_note" : "carriers_raben_test_credentials_note")}
            </p>
            {/* SOAP login (WSS UsernameToken) + the shipper's NIP. */}
            <div className="carrier-account-times">
                {([
                    ["loginUsername", "carriers_login_username", "name@company.pl"],
                    ["vatNumber", "carriers_vat_number", "1234567890"],
                ] as const).map(([key, labelKey, placeholder]) => (
                    <div className="admin-integrations-field" key={key}>
                        <label className="admin-integrations-field-label">{t(labelKey)}</label>
                        <input
                            className="admin-integrations-input admin-integrations-input--mono"
                            defaultValue={account[key]}
                            placeholder={placeholder}
                            disabled={disabled}
                            onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v !== account[key]) patch({ [key]: v || null }).catch(() => {});
                            }}
                        />
                    </div>
                ))}
            </div>
            <p className="admin-integrations-field-hint">{t("carriers_login_username_hint_raben")}</p>
            <SecretField
                label={t("carriers_password")}
                hint={t("carriers_password_hint")}
                secret={account.password}
                minLength={1}
                onSave={(plaintext) => patch({ password: plaintext })}
                onClear={() => patch({ password: null })}
            />

            {/* EDI header, payer, product. */}
            <h4 className="admin-integrations-field-label">{t("carriers_raben_section_edi")}</h4>
            <div className="carrier-field-grid">
                {textField("ediSender", "carriers_raben_edi_sender", "JLI EU")}
                {textField("ediReceiver", "carriers_raben_edi_receiver", RABEN_DEFAULTS.ediReceiver)}
                {textField("department", "carriers_raben_department", "")}
                {textField("payerSearchname", "carriers_raben_payer_searchname", "")}
                {textField("ediCustomerNumber", "carriers_raben_edi_customer_number", RABEN_DEFAULTS.ediCustomerNumber)}
                {textField("shipFromSearchname", "carriers_raben_ship_from_searchname", RABEN_DEFAULTS.shipFromSearchname)}
                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label" htmlFor={`raben-${account.id}-productType`}>{t("carriers_raben_product_type")}</label>
                    <select
                        id={`raben-${account.id}-productType`}
                        className="admin-integrations-select"
                        value={cfg.productType}
                        disabled={disabled}
                        onChange={(e) => commit(current({ productType: e.target.value as RabenProductType }))}
                    >
                        <option value="PROD01">{t("carriers_raben_product_prod01")}</option>
                        <option value="PROD02">{t("carriers_raben_product_prod02")}</option>
                    </select>
                </div>
            </div>
            <p className="admin-integrations-field-hint">{t("carriers_raben_edi_hint")}</p>

            {/* Additional services: uppercase free-text chips. */}
            <div className="admin-integrations-field">
                <label className="admin-integrations-field-label" htmlFor={`raben-${account.id}-services`}>{t("carriers_raben_services")}</label>
                <div className="admin-integrations-alias-chips">
                    {cfg.services.map((code) => (
                        <span className="admin-integrations-alias-chip" key={code}>
                            {code}
                            <button
                                type="button"
                                className="admin-integrations-alias-remove"
                                disabled={disabled}
                                aria-label={t("carriers_raben_service_remove", { code })}
                                onClick={() => removeService(code)}
                            >
                                <X size={12} />
                            </button>
                        </span>
                    ))}
                </div>
                <div className="admin-integrations-test-row">
                    <input
                        id={`raben-${account.id}-services`}
                        className="admin-integrations-input admin-integrations-input--mono"
                        value={serviceDraft}
                        placeholder="ROD"
                        disabled={disabled}
                        onChange={(e) => setServiceDraft(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addService(); } }}
                    />
                    <button type="button" className="btn btn-sm" disabled={disabled || !serviceDraft.trim()} onClick={addService}>
                        {t("carriers_raben_service_add")}
                    </button>
                </div>
                <p className="admin-integrations-field-hint">{t("carriers_raben_services_hint")}</p>
            </div>

            {/* INF notes sent on every shipment: one per line. */}
            <div className="admin-integrations-field">
                <label className="admin-integrations-field-label" htmlFor={`raben-${account.id}-notes`}>{t("carriers_raben_notes")}</label>
                <textarea
                    id={`raben-${account.id}-notes`}
                    className="admin-integrations-input"
                    rows={3}
                    value={notesText}
                    disabled={disabled}
                    onChange={(e) => setNotesText(e.target.value)}
                    onBlur={flush}
                />
                <p className="admin-integrations-field-hint">{t("carriers_raben_notes_hint", { max: String(RABEN_NOTES_MAX), length: String(RABEN_NOTE_MAX_LENGTH) })}</p>
                {errors.notes && <p className="admin-integrations-field-error">{t(errors.notes, { max: String(RABEN_NOTES_MAX), length: String(RABEN_NOTE_MAX_LENGTH) })}</p>}
            </div>

            {/* eWZ document upload over SFTP (optional). */}
            <h4 className="admin-integrations-field-label">{t("carriers_raben_section_ewz")}</h4>
            <div className="admin-integrations-form-row--toggle">
                <label className="admin-settings-toggle">
                    <input
                        type="checkbox"
                        aria-label={t("carriers_raben_ewz_enabled")}
                        checked={cfg.ewz.enabled}
                        disabled={disabled}
                        onChange={(e) => commitEwz({ enabled: e.target.checked })}
                    />
                    <span className="admin-settings-slider" />
                </label>
                <span className="admin-integrations-toggle-label">{t("carriers_raben_ewz_enabled")}</span>
            </div>
            <p className="admin-integrations-field-hint">{t("carriers_raben_ewz_hint")}</p>
            {cfg.ewz.enabled && (
                <>
                    <div className="carrier-field-grid">
                        <div className="admin-integrations-field">
                            <label className="admin-integrations-field-label" htmlFor={`raben-${account.id}-ewz-host`}>{t("carriers_raben_ewz_host")}</label>
                            <input
                                id={`raben-${account.id}-ewz-host`}
                                className="admin-integrations-input admin-integrations-input--mono"
                                value={cfg.ewz.host}
                                disabled={disabled}
                                onChange={(e) => setCfg({ ...cfg, ewz: { ...cfg.ewz, host: e.target.value } })}
                                onBlur={flush}
                            />
                        </div>
                        <div className="admin-integrations-field">
                            <label className="admin-integrations-field-label" htmlFor={`raben-${account.id}-ewz-port`}>{t("carriers_raben_ewz_port")}</label>
                            <input
                                id={`raben-${account.id}-ewz-port`}
                                className="admin-integrations-input admin-integrations-input--mono"
                                type="number"
                                min={1}
                                max={65535}
                                step={1}
                                value={portText}
                                disabled={disabled}
                                onChange={(e) => setPortText(e.target.value)}
                                onBlur={flush}
                            />
                            {errors.port && <p className="admin-integrations-field-error">{t(errors.port)}</p>}
                        </div>
                        <div className="admin-integrations-field">
                            <label className="admin-integrations-field-label" htmlFor={`raben-${account.id}-ewz-username`}>{t("carriers_raben_ewz_username")}</label>
                            <input
                                id={`raben-${account.id}-ewz-username`}
                                className="admin-integrations-input admin-integrations-input--mono"
                                value={cfg.ewz.username}
                                disabled={disabled}
                                onChange={(e) => setCfg({ ...cfg, ewz: { ...cfg.ewz, username: e.target.value } })}
                                onBlur={flush}
                            />
                        </div>
                        <div className="admin-integrations-field">
                            <label className="admin-integrations-field-label" htmlFor={`raben-${account.id}-ewz-relation`}>{t("carriers_raben_ewz_relation_number")}</label>
                            <input
                                id={`raben-${account.id}-ewz-relation`}
                                className="admin-integrations-input admin-integrations-input--mono"
                                value={cfg.ewz.relationNumber}
                                maxLength={RABEN_RELATION_LENGTH}
                                disabled={disabled}
                                onChange={(e) => setCfg({ ...cfg, ewz: { ...cfg.ewz, relationNumber: e.target.value } })}
                                onBlur={flush}
                            />
                            {errors.relationNumber && <p className="admin-integrations-field-error">{t(errors.relationNumber, { n: String(RABEN_RELATION_LENGTH) })}</p>}
                        </div>
                        <div className="admin-integrations-field">
                            <label className="admin-integrations-field-label" htmlFor={`raben-${account.id}-ewz-path`}>{t("carriers_raben_ewz_remote_path")}</label>
                            <input
                                id={`raben-${account.id}-ewz-path`}
                                className="admin-integrations-input admin-integrations-input--mono"
                                value={cfg.ewz.remotePath}
                                disabled={disabled}
                                onChange={(e) => setCfg({ ...cfg, ewz: { ...cfg.ewz, remotePath: e.target.value } })}
                                onBlur={flush}
                            />
                        </div>
                    </div>
                    <SecretField
                        label={t("carriers_raben_sftp_password")}
                        hint={t("carriers_raben_sftp_password_hint")}
                        secret={account.apiKey}
                        minLength={1}
                        onSave={(plaintext) => patch({ apiKey: plaintext })}
                        onClear={() => patch({ apiKey: null })}
                    />
                </>
            )}
        </>
    );
};

export default RabenAccountFields;
